import { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js";
import "./QuestionAnalysis.css"; // Import the separate CSS file here

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Cohere palette generator based on your data count
const generateColor = (index, total) => {
  const hue = (index * (360 / Math.max(total, 1))) % 360;
  return `hsl(${hue}, 70%, 55%)`;
};

export default function QuestionAnalysis({ reportData = [] }) {
  /* ---------- DYNAMIC DATA PROCESSING ---------- */
  const { pieData, tableRows, totalStudents } = useMemo(() => {
    // 1. Find max problems solved dynamically
    let maxSolved = 0;
    reportData.forEach(student => {
      const solved = student.problemsSolved || 0;
      if (solved > maxSolved) maxSolved = solved;
    });

    // 2. Initialize exact frequency counters (0 up to maxSolved)
    const frequencies = Array(maxSolved + 1).fill(0);

    // 3. Count frequencies
    reportData.forEach(student => {
      const solved = student.problemsSolved || 0;
      frequencies[solved]++;
    });

    // 4. Generate configurations
    const labels = frequencies.map((_, index) => `${index} Solved`);
    const backgroundColors = frequencies.map((_, index) => generateColor(index, frequencies.length));

    const chartData = {
      labels,
      datasets: [
        {
          data: frequencies,
          backgroundColor: backgroundColors,
          borderWidth: 1
        }
      ]
    };

    const rows = frequencies.map((count, index) => ({
      label: `${index} Solved`,
      count,
      color: backgroundColors[index]
    }));

    return {
      pieData: chartData,
      tableRows: rows,
      totalStudents: reportData.length
    };
  }, [reportData]);

  return (
    <div className="analysis-card">
      <div className="analysis-header">
        <h3 className="analysis-title">Question Solving Analysis</h3>
        <p className="analysis-subtitle">Dynamic student distribution based on data values</p>
      </div>

      <div className="dashboard-grid">
        {/* Chart Section */}
        <div className="chart-wrapper">
          <Pie data={pieData} options={{ responsive: true, maintainAspectRatio: false }} />
        </div>

        {/* Data Table Section */}
        <div className="table-wrapper">
          <table className="analysis-table">
            <thead>
              <tr className="th-row">
                <th className="th-left">Questions Solved</th>
                <th className="th-right">Student Count</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, index) => (
                <tr key={index}>
                  <td className="td-left">
                    <span className="color-dot" style={{ backgroundColor: row.color }}></span> 
                    {row.label}
                  </td>
                  <td className="td-right">{row.count}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td className="td-total-left">Total Students</td>
                <td className="td-total-right">{totalStudents}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}